import React from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Paginate from "../../ui/paginate";

interface IDataTable<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  current_page?: number;
  last_page?: number;
  onNext?: (page: number) => void;
  onPrev?: (page: number) => void;
  hasPaginate?: boolean;
}

function DataTable<TData, TValue>({
  columns,
  data = [] as TData[],
  current_page,
  last_page,
  onNext,
  onPrev,
  hasPaginate = true,
}: IDataTable<TData, TValue>) {
  const { getHeaderGroups, getRowModel } = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full caption-bottom text-sm">
          <thead>
            {getHeaderGroups().map((headerGroup) => (
              <tr
                key={headerGroup.id}
                className="border-b border-gray-100 bg-gray-100/20"
              >
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-3.5 text-left align-middle text-xs font-semibold uppercase tracking-wider text-gray-500 last:text-right whitespace-nowrap"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-100">
            {getRowModel().rows?.length ? (
              getRowModel().rows.map((row, index) => (
                <tr
                  key={row.id}
                  className={`transition-colors duration-150 hover:bg-blue-50/40 ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50/40"
                  }`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-4 py-2.5 align-middle text-sm text-gray-700 last:flex last:items-center last:justify-end whitespace-nowrap"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="h-32 text-center align-middle whitespace-nowrap"
                >
                  <div className="flex flex-col items-center justify-center gap-1.5 text-gray-400">
                    <svg
                      className="h-8 w-8 opacity-40"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                      />
                    </svg>
                    <span className="text-sm font-medium">
                      No results found
                    </span>
                    <span className="text-xs">Try adjusting your filters</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {hasPaginate && (
        <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-3 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Page{" "}
            <span className="font-medium text-gray-700">
              {current_page ?? 1}
            </span>{" "}
            of{" "}
            <span className="font-medium text-gray-700">{last_page ?? 1}</span>
          </p>
          <Paginate
            current_page={current_page || 1}
            last_page={last_page || 1}
            onNext={onNext || (() => {})}
            onPrev={onPrev || (() => {})}
          />
        </div>
      )}
    </div>
  );
}

export default DataTable;
